import { useMemo, type ReactNode } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';

/**
 * Slice I — composición de los providers de OAuth (Google + Microsoft) alrededor del árbol de
 * la app. Cada provider se monta SIEMPRE (no condicional) porque montar/desmontar por env var
 * causaría re-renders raros del árbol. La feature-flag de "hay client id válido" se chequea en
 * cada botón vía <see cref="googleClientId"/> / <see cref="microsoftClientId"/> — si están
 * vacíos, el botón se esconde.
 *
 * Para no romper la red de tests si el env var no está, usamos un placeholder válido (`-`)
 * que GoogleOAuthProvider acepta como "anything goes"; sin ese placeholder el provider tira
 * un warning al mount. El backend igual rechaza tokens con 503 si no hay client id configurado.
 */
export const googleClientId = (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined) ?? '';
export const microsoftClientId = (import.meta.env.VITE_MICROSOFT_CLIENT_ID as string | undefined) ?? '';
/** sessionStorage key para persistir la opción comercial durante el redirect flow de Microsoft. */
export const OAUTH_PERSONA_KEY = 'tilde.oauth.persona';
export const microsoftTenantId =
  (import.meta.env.VITE_MICROSOFT_TENANT_ID as string | undefined) ?? 'common';

interface OAuthProvidersProps {
  children: ReactNode;
}

export function OAuthProviders({ children }: OAuthProvidersProps): JSX.Element {
  // MSAL requiere una única instancia compartida por la app. Memo para evitar re-crear el client
  // en cada render (cada `new PublicClientApplication` registra event handlers internos).
  const msalInstance = useMemo(() => {
    // Si no hay clientId de Microsoft configurado, igual creamos una instancia "stub" para que
    // MsalProvider no tire — el botón de Microsoft se va a esconder y el flow nunca se dispara.
    const clientId = microsoftClientId || '00000000-0000-0000-0000-000000000000';
    const authority = `https://login.microsoftonline.com/${microsoftTenantId || 'common'}`;
    const instance = new PublicClientApplication({
      auth: {
        clientId,
        authority,
        // Personal Microsoft accounts (MSA) + AAD — habilita "common" + multi-tenant.
        knownAuthorities: ['login.microsoftonline.com'],
        // Redirect flow: la ventana completa va a MS y vuelve al origin (el SPA), donde
        // handleRedirectPromise procesa el #code en el MISMO tab (el verifier PKCE está en su
        // sessionStorage). Sin popup → sin el problema de monitoreo opener↔popup de MSAL v5.
        redirectUri: typeof window !== 'undefined' ? window.location.origin : '/',
      },
      cache: {
        // In-memory parity con tokenStorage de Slice 13a (sin localStorage por XSS). MSAL v5
        // soporta 'sessionStorage' como location — el id_token solo lo necesitamos para hacer
        // el call al backend; después de eso, vive en `tokenStorage` (in-memory) y MSAL puede
        // descartarlo.
        cacheLocation: 'sessionStorage',
      },
    });
    // initialize() debe correr antes del primer acquireTokenPopup; lo disparamos en background.
    void instance.initialize();
    return instance;
  }, []);

  return (
    <GoogleOAuthProvider clientId={googleClientId || 'placeholder.apps.googleusercontent.com'}>
      <MsalProvider instance={msalInstance}>{children}</MsalProvider>
    </GoogleOAuthProvider>
  );
}
